import { useState } from 'react';
import {
  OVERVIEW_ACTION_CATEGORY_LABELS,
  OVERVIEW_MODULE_LABELS,
  overviewTargetPath,
  type OverviewAction,
  type OverviewActionCategory,
  type OverviewNextAction,
} from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { CtaLink, useLookOnlyReason } from './parts.js';

const ORDER: readonly OverviewActionCategory[] = ['BLOCKING', 'REVIEW', 'RECOMMENDED'];

/** How many lines show before "View all": the most urgent ones. */
export const ACTION_CENTER_VISIBLE = 3;

/**
 * Whether the next-action panel already says this action. An ACT panel IS one
 * action (the server copies its title and its one CTA); a CHOOSE panel offers
 * the ways on as its CTAs, so an action whose CTA goes to one of them is said
 * there too. Saying it twice on one page is what this list must not do.
 */
export function shownAsNextAction(action: OverviewAction, next: OverviewNextAction, address: string): boolean {
  if (next.kind === 'ACT') return action.title === next.title;
  if (next.kind !== 'CHOOSE') return false;
  const path = overviewTargetPath(address, action.cta.target);

  return next.ctas.some((cta) => overviewTargetPath(address, cta.target) === path);
}

/**
 * EVERYTHING ELSE THAT WAITS ON A PERSON, in the server's order.
 *
 * Blocking first, then what needs a human decision, then what is merely worth
 * doing — and within each, by severity and reach, never by age. Each line is
 * already AGGREGATED (eight props are one line that says 8); the screen it opens
 * lists them. The list re-sorts and counts nothing itself.
 *
 * SECONDARY BY DESIGN (redesign of 2026-10-30): the action the next-action panel
 * shows is left out here, and the first three lines show; the rest open in place
 * under "View all". Three is what a glance takes in; the order already put the
 * most urgent first.
 */
export function ActionCenter({
  actions: all,
  next,
  address,
  readOnly,
}: {
  actions: readonly OverviewAction[];
  next: OverviewNextAction;
  address: string;
  readOnly: boolean;
}) {
  const reason = useLookOnlyReason(readOnly);
  const [expanded, setExpanded] = useState(false);
  const actions = all.filter((a) => !shownAsNextAction(a, next, address));
  const visible = new Set((expanded ? actions : actions.slice(0, ACTION_CENTER_VISIBLE)).map((a) => a.id));
  const hidden = actions.length - visible.size;
  const groups = ORDER.map((category) => ({
    category,
    items: actions.filter((a) => a.category === category && visible.has(a.id)),
    total: actions.filter((a) => a.category === category).length,
  })).filter((g) => g.items.length > 0);

  return (
    <section className="cc-panel" data-testid="overview-action-center" aria-labelledby="overview-actions-title">
      <div className="cc-panel-head">
        <h2 className="cc-panel-title" id="overview-actions-title">
          <Icon name="checklist" size={18} />
          Action center
        </h2>
        <span className="cc-panel-sub" data-testid="overview-action-count">
          {actions.length === 0 ? '' : `${String(actions.length)} ${actions.length === 1 ? 'item' : 'items'}`}
        </span>
      </div>

      {groups.length === 0 ? (
        <div data-testid="overview-actions-empty">
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>Nothing else needs you</p>
          <p className="cc-note" style={{ margin: '6px 0 0', fontSize: 12.5 }}>
            {all.length > 0 ? 'The next action above is the only open item.' : 'Your reviews are up to date.'}
          </p>
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.category} className="cc-actions-group" data-testid={`overview-actions-${group.category}`}>
            <h3 className="cc-actions-head">
              {OVERVIEW_ACTION_CATEGORY_LABELS[group.category]}
              <span className="cc-actions-count">{group.total}</span>
            </h3>
            <ul className="cc-actions-list">
              {group.items.map((action) => {
                const refusal = reason(action.cta);
                return (
                  <li key={action.id} className="cc-action" data-testid={`overview-action-${action.id}`}>
                    <span className="cc-action-stripe" data-category={action.category} aria-hidden="true" />
                    <div style={{ minWidth: 0 }}>
                      <p className="cc-action-title">{action.title}</p>
                      <p className="cc-action-desc">{action.description}</p>
                      <div className="cc-action-foot">
                        <CtaLink
                          address={address}
                          cta={action.cta}
                          className="cc-link"
                          testId={`overview-action-${action.id}-cta`}
                        />
                        <span className="cc-action-module">{OVERVIEW_MODULE_LABELS[action.module]}</span>
                      </div>
                      {refusal === null ? null : (
                        <p className="cc-note" style={{ margin: '6px 0 0' }} data-testid={`overview-action-${action.id}-refusal`}>
                          {refusal}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))
      )}

      {hidden > 0 || (expanded && actions.length > ACTION_CENTER_VISIBLE) ? (
        <button
          type="button"
          className="cc-link cc-more"
          aria-expanded={expanded}
          data-testid="overview-actions-more"
          onClick={() => {
            setExpanded((v) => !v);
          }}
        >
          {expanded ? 'Show fewer' : `View all (${String(actions.length)})`}
          <Icon name={expanded ? 'chevronDown' : 'arrowRight'} size={14} />
        </button>
      ) : null}
    </section>
  );
}
